import type { CardContentKind } from "@-/dev.improve.haniverse.lib.cards.card-content";

/**
 * Where the learner is studying right now, picked from the Learn header's Environment menu, in
 * the menu's order. An environment decides which card kinds the review queue serves and whether
 * the deck runs in Touch or Voice mode:
 *
 * - `standard` — every card kind the user's settings enable.
 * - `silent` — no audio and no microphone: reveal audio is muted and mic drills are withheld.
 * - `no-speaking` / `no-headphones` — every card kind, for now.
 * - `hands-free` — glyph → gloss cards, answered by voice (the only environment in Voice mode).
 */
export const STUDY_ENVIRONMENTS = [
  "standard",
  "silent",
  "no-speaking",
  "no-headphones",
  "hands-free",
] as const;

export type StudyEnvironment = (typeof STUDY_ENVIRONMENTS)[number];

export function studyEnvironmentLabel(studyEnvironment: StudyEnvironment): string {
  switch (studyEnvironment) {
    case "standard": {
      return "Standard Study";
    }
    case "silent": {
      return "Silent Study";
    }
    case "no-speaking": {
      return "No Speaking";
    }
    case "no-headphones": {
      return "No Headphones";
    }
    case "hands-free": {
      return "Hands-Free";
    }
  }
}

/** Short line under the Environment menu label, saying what that environment changes. */
export function studyEnvironmentDescription(studyEnvironment: StudyEnvironment): string {
  switch (studyEnvironment) {
    case "standard": {
      return "Your usual reviews, with sound and speaking.";
    }
    case "silent": {
      return "Quiet study — audio is muted and speaking drills are skipped.";
    }
    case "no-speaking": {
      return "Skip speaking drills. You won't need a microphone.";
    }
    case "no-headphones": {
      return "Reviews that don't ask you to listen.";
    }
    case "hands-free": {
      return "Voice-only meaning review. Answer out loud, no tapping.";
    }
  }
}

/** Hands-Free is the only environment the deck studies by voice. */
export function studyEnvironmentStudyMode(studyEnvironment: StudyEnvironment): "manual" | "voice" {
  return studyEnvironment === "hands-free" ? "voice" : "manual";
}

/**
 * Whether the environment lets this card kind into the review queue. Applied on top of the user's
 * per-kind settings (`isCardContentKindReviewEligible`), which it can only narrow.
 */
export function isCardContentKindAllowedInStudyEnvironment({
  cardContentKind,
  studyEnvironment,
}: {
  cardContentKind: CardContentKind;
  studyEnvironment: StudyEnvironment;
}): boolean {
  switch (studyEnvironment) {
    case "standard":
    case "no-speaking":
    case "no-headphones": {
      return true;
    }
    case "silent": {
      // Every typed drill works muted; only the spoken sentence drill needs the microphone.
      return cardContentKind !== "word-pronunciation-in-sentence";
    }
    case "hands-free": {
      return cardContentKind === "grapheme-meaning";
    }
  }
}
